/**
 * SOFTSTORM — Gallery Section JavaScript
 * Infinite scroll with robust batch loading
 */
(function () {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  class Gallery {
    constructor(el) {
      this.section       = el;
      this.sectionId     = el.dataset.sectionId;
      this.grid          = $(`#gallery-grid-${this.sectionId}`, el);
      this.sentinel      = $(`#gallery-sentinel-${this.sectionId}`, el);
      this.filterBtns    = $$('.gallery-filter-btn', el);
      this.batchSize     = parseInt(el.dataset.imagesPerPage, 10) || 20;
      this.allItems      = $$('.gallery-item', this.grid);
      this.filteredItems = [...this.allItems];
      this.loadedCount   = 0;
      this.isLoading     = false;

      this._setGridCSSVars();
      this._hideAll();
      this._showBatch();
      this._initInfiniteScroll();
      this._initFilter();
    }

    _setGridCSSVars() {
      const s = this.section.dataset;
      this.grid.style.setProperty('--gallery-columns',        s.columnsDesktop || 4);
      this.grid.style.setProperty('--gallery-columns-tablet', s.columnsTablet  || 2);
      this.grid.style.setProperty('--gallery-columns-mobile', s.columnsMobile  || 1);
      this.grid.style.setProperty('--gallery-gap',            (s.gap || 8) + 'px');
    }

    _hideAll() {
      this.filteredItems.forEach(item => item.classList.add('is-hidden'));
      this.loadedCount = 0;
    }

    // Immediately swap data-src / data-srcset → real attributes
    // No observer needed — called right when item becomes visible
    _revealImage(img) {
      const picture = img.closest('picture');
      if (picture) {
        $$('source[data-srcset]', picture).forEach(source => {
          source.srcset = source.dataset.srcset || '';
          source.removeAttribute('data-srcset');
        });
      }
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
      img.onload = () => {
        img.classList.remove('lazyload');
        img.classList.add('lazyloaded');
      };
      img.onerror = () => {
        img.classList.remove('lazyload');
      };
      // If image already cached, onload won't fire — handle immediately
      if (img.complete && img.naturalWidth > 0) {
        img.classList.remove('lazyload');
        img.classList.add('lazyloaded');
      }
    }

    _showBatch(animate = false) {
      const next = this.filteredItems.slice(this.loadedCount, this.loadedCount + this.batchSize);
      if (next.length === 0) {
        this._hideSentinel();
        return;
      }

      next.forEach((item, i) => {
        const delay = animate ? i * 25 : 0;
        setTimeout(() => {
          item.classList.remove('is-hidden');
          if (animate) {
            item.classList.add('is-animating-in');
            item.addEventListener('animationend', () => {
              item.classList.remove('is-animating-in');
            }, { once: true });
          }
          // Load ALL images in this item immediately on reveal
          $$('img', item).forEach(img => this._revealImage(img));
        }, delay);
      });

      this.loadedCount += next.length;

      if (this.loadedCount >= this.filteredItems.length) {
        // Small delay to let last batch render before hiding sentinel
        setTimeout(() => this._hideSentinel(), next.length * 25 + 100);
      } else {
        this._showSentinel();
      }
    }

    _hideSentinel() {
      if (this.sentinel) this.sentinel.style.display = 'none';
    }

    _showSentinel() {
      if (this.sentinel) this.sentinel.style.display = 'block';
    }

    _initInfiniteScroll() {
      if (!this.sentinel || !('IntersectionObserver' in window)) return;

      this._scrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (!entry.isIntersecting || this.isLoading) return;
            if (this.loadedCount >= this.filteredItems.length) return;

            this.isLoading = true;
            this._showBatch(true);
            setTimeout(() => { this.isLoading = false; }, 400);
          });
        },
        { rootMargin: '300px 0px', threshold: 0 }
      );

      this._scrollObserver.observe(this.sentinel);
    }

    _initFilter() {
      if (!this.filterBtns.length) return;
      this.filterBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          this.filterBtns.forEach(b => {
            b.classList.toggle('active', b === btn);
            b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
          });
          const filter = btn.dataset.filter;
          this.filteredItems = filter === 'all'
            ? [...this.allItems]
            : this.allItems.filter(item => item.dataset.tag === filter);

          this.allItems.forEach(item => item.classList.add('is-hidden'));
          this.loadedCount = 0;
          this._showBatch(true);
        });
      });
    }
  }

  function initAll() {
    $$('.gallery-section').forEach(el => {
      if (!el._galleryInstance) el._galleryInstance = new Gallery(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', e => {
    const el = e.target.querySelector('.gallery-section');
    if (el) { el._galleryInstance = null; el._galleryInstance = new Gallery(el); }
  });

})();
